/** The console's organisation scope.
 *
 * This is a FILTER, not a tenant switch. CLAUDE.md invariant 1 says tenant
 * filtering lives in one queryset manager and a client-supplied `org_id` is
 * never trusted; invariant 2 puts identity in the session. So the control here
 * only ever appends `?organization=` to a request, and the server decides what
 * that returns — narrowing what an agent already has access to, never widening
 * it. Choosing an organisation you have no tickets in yields an empty queue,
 * which is the behaviour a test pins down.
 *
 * The choice is remembered per browser because an agent who works one
 * organisation's queue all morning should not re-pick it on every reload.
 */
import { useQuery } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { type AgentOrganization, agentApi } from './api'

const STORAGE_KEY = 'helpdesk.orgScope'

interface OrgScopeValue {
  /** null means "every organisation" — the default. */
  orgId: string | null
  setOrgId: (id: string | null) => void
  organizations: AgentOrganization[]
  current: AgentOrganization | null
  isLoading: boolean
}

const OrgScopeContext = createContext<OrgScopeValue | null>(null)

function readStored(): string | null {
  return localStorage.getItem(STORAGE_KEY)
}

export function OrgScopeProvider({ children }: { children: ReactNode }) {
  const [orgId, setOrgIdState] = useState<string | null>(readStored)

  const { data: organizations = [], isLoading } = useQuery({
    queryKey: ['agent-organizations'],
    queryFn: () => agentApi.get<AgentOrganization[]>('/organizations/'),
    staleTime: 5 * 60_000,
  })

  const setOrgId = useCallback((id: string | null) => {
    if (id) localStorage.setItem(STORAGE_KEY, id)
    else localStorage.removeItem(STORAGE_KEY)
    setOrgIdState(id)
  }, [])

  const current = useMemo(
    () => organizations.find((org) => String(org.id) === orgId) ?? null,
    [organizations, orgId],
  )

  const value = useMemo(
    () => ({ orgId, setOrgId, organizations, current, isLoading }),
    [orgId, setOrgId, organizations, current, isLoading],
  )

  return <OrgScopeContext.Provider value={value}>{children}</OrgScopeContext.Provider>
}

export function useOrgScope(): OrgScopeValue {
  const context = useContext(OrgScopeContext)
  if (!context) throw new Error('useOrgScope must be used inside an OrgScopeProvider')
  return context
}

/** Returns the scope as a query parameter, ready to merge into a request. */
export function useOrgScopeParam(): string | null {
  return useOrgScope().orgId
}
